import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react"
import { SortableKey } from "@/hooks/useSurveySort"

/** Sort direction: ascending or descending. */
export type SortDirection = "asc" | "desc"

/**
 * Generic sort configuration used by SortableColumnHeader.
 * The key type defaults to string so it can be used with any column key union.
 */
export interface GenericSortConfig<K extends string = string> {
  key: K
  direction: SortDirection
}

interface SortableColumnHeaderProps<K extends string = SortableKey> {
  /** The column key this header represents. */
  columnKey: K
  /** Display label for the column. */
  label: string
  /** Current active sort configuration (null = no sort active). */
  sortConfig: GenericSortConfig<K> | null
  /** Callback to toggle sorting when the header is clicked. */
  onToggleSort: (key: K) => void
}

/**
 * A clickable column header that displays the current sort direction.
 *
 * Shows an up arrow for ascending, a down arrow for descending,
 * and a neutral up-down arrow when the column is not actively sorted.
 * Generic over the column key type so it can be used in any table.
 *
 * @param props - The component properties.
 * @returns The rendered sortable header content (inline, no wrapping element).
 */
function SortableColumnHeader<K extends string = SortableKey>({
  columnKey,
  label,
  sortConfig,
  onToggleSort,
}: SortableColumnHeaderProps<K>): React.ReactElement {
  const isActive = sortConfig?.key === columnKey
  const direction = isActive ? sortConfig.direction : null

  return (
    <button
      type="button"
      onClick={() => onToggleSort(columnKey)}
      className="inline-flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer"
      aria-label={`Sort by ${label}`}
    >
      {label}
      {direction === "asc" && <ArrowUp className="size-3.5" />}
      {direction === "desc" && <ArrowDown className="size-3.5" />}
      {!direction && <ArrowUpDown className="size-3.5 opacity-50" />}
    </button>
  )
}

export default SortableColumnHeader
